import { useEffect, useState, useRef } from "react";
import styles from "./ChatSection.module.css";
import useDashboardStore from "../../store/dashboardStore";
import chatStore from "../../store/chatStore";
import useAuthStore from "../../store/authStore";
import api from "../../services/api";
import { ENDPOINTS } from "../../utils/endpoints";
import { FiSearch } from "react-icons/fi";
import MessageCard from "./MessageCard";
import EmojiPicker from "emoji-picker-react";
import { toast } from "react-toastify";
import { confirmAlert } from "react-confirm-alert";

import {
  FaLock,
  FaUnlock,
  FaVolumeUp,
  FaVolumeMute,
  FaArrowDown,
  FaSpinner,
  FaChevronDown,
  FaChevronUp,
  FaInfoCircle,
  FaTimes,
} from "react-icons/fa";
import axios from "axios";
import { formatDateTimeParts } from "./MessageCard";

const MESSAGES_PER_PAGE = 20;

const ChatSection = () => {
  const { user, userMeta, socket, refreshIapStatus } = useAuthStore();
  const {
    channels,
    setChannels,
    selectedChannel,
    setSelectedChannel,
    users,
    setUsers,
    userJoinedChannels,
    setUserJoinedChannels,
  } = useDashboardStore();
  const {
    unreadChatCounts,
    setUnreadChatCounts,
    newMessageRecieved,
    setNewMessageRecieved,
    newChatPermissionRecieved,
    setNewChatPermissionRecieved,
    notificationPermissionList,
    setNotificationPermissionList,
    newChatNotificationRecieved,
    setNewChatNotificationRecieved,
  } = chatStore();

  const [isAccessible, setIsAccessible] = useState(false);
  const [amIAssignedToThisChannel, setAmIAssignedToThisChannel] =
    useState(false);
  const [loading, setLoading] = useState(true);
  const [joinedRoom, setJoinedRoom] = useState(false);

  const [messages, setMessages] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [scrollToBottom, setScrollToBottom] = useState(false);
  const [newMessage, setNewMessage] = useState("");
  const [isLoadingPlans, setIsLoadingPlans] = useState(false);

  const [isSending, setIsSending] = useState(false);

  const messageListRef = useRef(null);
  const unreadRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const inputRef = useRef(null);

  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  // Debounce ref to prevent rapid consecutive sends
  const lastSendTime = useRef(0);
  const SEND_DEBOUNCE_MS = 500; // Minimum time between sends

  //To show the unread-message count
  const [retainedUnreadCount, setRetainedUnreadCount] = useState(null);

  const [linkPreview, setLinkPreview] = useState(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);

  const [uploading, setUploading] = useState(false); // state for loader
  const [imagePreview, setImagePreview] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  // const [previewCaption, setPreviewCaption] = useState("");

  const fileInputRef = useRef(null);
  const [replyingTo, setReplyingTo] = useState(null);

  /// ---------------------------------------------- Search Mode ------------------------------------------------
  const [searchMode, setSearchMode] = useState(false);
  const [contextMessages, setContextMessages] = useState([]);
  const [contextTarget, setContextTarget] = useState(null); // id of the clicked search result
  const searchRef = useRef(null);

  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);

  const [highlightedMessageId, setHighlightedMessageId] = useState(null);
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [showPlanSelection, setShowPlanSelection] = useState(false);
  const [availablePlans, setAvailablePlans] = useState([]);

  const [channelDetails, setChannelDetails] = useState({});

  // Channel info states
  const [showChannelInfo, setShowChannelInfo] = useState(true);
  const [showChannelInfoModal, setShowChannelInfoModal] = useState(false);

  // Load channel info visibility from localStorage
  useEffect(() => {
    if (selectedChannel?.id) {
      const savedState = localStorage.getItem(
        `channelInfo_${selectedChannel.id}`,
      );
      if (savedState !== null) {
        setShowChannelInfo(savedState === "true");
      } else {
        setShowChannelInfo(true); // Default to open
      }
    }
  }, [selectedChannel?.id]);

  // Clear reply state when changing channels
  useEffect(() => {
    setReplyingTo(null);
  }, [selectedChannel.id]);

  // Refresh IAP status on mount (sync mobile purchases to web)
  useEffect(() => {
    refreshIapStatus();
  }, []);

  const jumpToMessageFromReply = async (messageId) => {
    try {
      const res = await api.get(
        ENDPOINTS.GET_MESSAGES_BY_CONTEXT(
          selectedChannel.id,
          user.id,
          messageId,
          10,
          10,
        ),
      );
      const msgs = res.data.messages || [];
      setContextMessages(msgs);
      setSearchMode(true);
      setContextTarget(messageId);
      setHighlightedMessageId(messageId);

      setTimeout(() => {
        setSearch("");
        const el = document.getElementById(`msg-${messageId}`);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });

          // Remove highlight after 3 seconds
          setTimeout(() => {
            setHighlightedMessageId(null);
          }, 3000);
        }
      }, 100);
    } catch (err) {
      console.error("Jump to reply failed:", err);
      toast.error("Could not find the original message");
    }
  };

  // Focus input when replying
  useEffect(() => {
    if (replyingTo && inputRef.current) {
      inputRef.current.focus();
    }
  }, [replyingTo]);

  // Toggle channel info visibility
  const toggleChannelInfo = () => {
    const newState = !showChannelInfo;
    setShowChannelInfo(newState);
    localStorage.setItem(`channelInfo_${selectedChannel.id}`, newState);
  };

  const handleEditMessage = async (messageId, newText) => {
    try {
      // console.log("Editing message:", { messageId, newText, userId: user.id });

      const res = await api.put(ENDPOINTS.EDIT_MESSAGE(messageId), {
        newMessage: newText,
        userId: user.id,
      });

      if (res.data.success) {
        // Update local state
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === messageId
              ? {
                  ...msg,
                  message: newText,
                  updated_at: new Date().toISOString(),
                }
              : msg,
          ),
        );

        // Update context messages if in search mode
        if (searchMode) {
          setContextMessages((prev) =>
            prev.map((msg) =>
              msg.id === messageId
                ? {
                    ...msg,
                    message: newText,
                    updated_at: new Date().toISOString(),
                  }
                : msg,
            ),
          );
        }

        // Emit socket event
        socket.emit("message-edited", {
          channelId: selectedChannel.id,
          messageId,
          newMessage: newText,
          userId: user.id,
        });

        toast.success("Message updated successfully");
      }
    } catch (error) {
      console.error("Failed to edit message:", error);
      console.error("Error response:", error.response?.data);
      toast.error(error.response?.data?.message || "Failed to edit message");
    }
  };

  const handleDeleteMessage = (messageId) => {
    confirmAlert({
      title: "Confirm Delete",
      message: "Are you sure you want to delete this message?",
      buttons: [
        {
          label: "Yes",
          onClick: async () => {
            try {
              const res = await api.delete(
                ENDPOINTS.DELETE_MESSAGE(messageId),
                {
                  data: { userId: user.id },
                },
              );

              if (res.data.success) {
                // Update local state
                setMessages((prev) =>
                  prev.map((msg) =>
                    msg.id === messageId ? { ...msg, is_deleted: 1 } : msg,
                  ),
                );

                // Update context messages if in search mode
                if (searchMode) {
                  setContextMessages((prev) =>
                    prev.map((msg) =>
                      msg.id === messageId ? { ...msg, is_deleted: 1 } : msg,
                    ),
                  );
                }

                // Emit socket event
                socket.emit("message-deleted", {
                  channelId: selectedChannel.id,
                  messageId,
                  userId: user.id,
                });

                toast.success("Message deleted successfully");
              }
            } catch (error) {
              console.error("Failed to delete message:", error);
              toast.error(
                error.response?.data?.message || "Failed to delete message",
              );
            }
          },
        },
        {
          label: "No",
          onClick: () => toast.info("Message deletion cancelled"),
        },
      ],
    });
  };

  useEffect(() => {
    if (!socket) return;

    // Listen for message edits
    const handleMessageEdited = (data) => {
      const { messageId, newMessage, channelId } = data;

      // Only update if it's for the current channel
      if (channelId === selectedChannel.id) {
        // Update normal messages
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === messageId
              ? {
                  ...msg,
                  message: newMessage,
                  updated_at: new Date().toISOString(),
                }
              : msg,
          ),
        );

        // Update context messages if in search mode
        if (searchMode) {
          setContextMessages((prev) =>
            prev.map((msg) =>
              msg.id === messageId
                ? {
                    ...msg,
                    message: newMessage,
                    updated_at: new Date().toISOString(),
                  }
                : msg,
            ),
          );
        }
      }
    };

    // Listen for message deletes
    const handleMessageDeleted = (data) => {
      const { messageId, channelId } = data;

      // Only update if it's for the current channel
      if (channelId === selectedChannel.id) {
        // Update normal messages
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === messageId ? { ...msg, is_deleted: 1 } : msg,
          ),
        );

        // Update context messages if in search mode
        if (searchMode) {
          setContextMessages((prev) =>
            prev.map((msg) =>
              msg.id === messageId ? { ...msg, is_deleted: 1 } : msg,
            ),
          );
        }
      }
    };

    socket.on("message-edited", handleMessageEdited);
    socket.on("message-deleted", handleMessageDeleted);

    // Cleanup
    return () => {
      socket.off("message-edited", handleMessageEdited);
      socket.off("message-deleted", handleMessageDeleted);
    };
  }, [socket, selectedChannel.id, searchMode]);

  const handleMembershipClick = async () => {
    if (isPurchasing || isLoadingPlans) return;

    setIsLoadingPlans(true);

    try {
      // Get channel details to find membership plans
      const { data } = await api.get(
        ENDPOINTS.GET_CHANNEL_BY_ID(selectedChannel.id),
      );
      const channelData = data.channel || {};
      const membershipIds = channelData.membership_ids || [];

      if (membershipIds.length === 0) {
        toast.error("No membership plans configured for this channel");
        return;
      }

      if (membershipIds.length === 1) {
        // Single plan - proceed directly
        handlePurchaseMembership(membershipIds[0]);
      } else {
        // Multiple plans - fetch all memberships and filter
        const { data: membershipsData } = await api.get(
          ENDPOINTS.GET_ALL_MEMBERSHIPS,
          { params: { hide_restricted: true } },
        );
        const allPlans = membershipsData.memberships || [];

        // Filter to only show plans that this channel uses
        const channelPlans = allPlans.filter((plan) =>
          membershipIds.includes(plan.id),
        );

        setAvailablePlans(channelPlans);
        setShowPlanSelection(true);
      }
    } catch (error) {
      console.error("Error fetching channel plans:", error);
      toast.error("Failed to load membership plans");
    } finally {
      setIsLoadingPlans(false);
    }
  };

  // --- helper to extract first URL ---
  const extractFirstUrl = (text) => {
    if (!text) return null;
    const urlRegex = /(https?:\/\/[^\s]+)/gi;
    const matches = text.match(urlRegex);
    return matches ? matches[0] : null;
  };

  // helper: check if user can send message
  const canUserSendMessage = () => {
    if (user?.roles[0] === "administrator") return true;
    if (user?.roles[0] === "trader" && amIAssignedToThisChannel) return true;
    return selectedChannel.allow_messages;
  };

  const handlePurchaseMembership = async (membershipPlanId) => {
    if (!membershipPlanId || isPurchasing) return;

    setIsPurchasing(true);

    try {
      const response = await api.post(ENDPOINTS.PURCHASE_LINK, {
        user_id: user.id,
        lid: membershipPlanId,
      });

      if (response.data.success) {
        window.open(response.data.redirect_url, "_blank");
      } else {
        toast.error(
          response.data.message || "Failed to generate purchase link",
        );
      }
    } catch (error) {
      console.error("Purchase error:", error);
      toast.error("Failed to initiate purchase. Please try again.");
    } finally {
      setIsPurchasing(false);
    }
  };

  //Check if Channel is Joined or Not
  const checkJoined = async () => {
    const isJoined = userJoinedChannels.filter(
      (s) => s.channel_id === selectedChannel.id,
    );
    if (isJoined.length > 0) {
      setJoinedRoom(true);
    } else {
      setJoinedRoom(false);
    }
  };

  //Join a Channel and add in UserJoinedChannels list
  const joinChannel = async () => {
    try {
      const res = await api.post(
        ENDPOINTS.JOIN_CHANNEL_CHAT(selectedChannel.id),
        { userId: user.id },
      );
      if (res.data.success) {
        const { data } = await api.get(ENDPOINTS.GET_JOINED_CHANNEL(user.id));
        if (data.success) {
          const channelsJoined = data.channels || [];
          setUserJoinedChannels(channelsJoined);

          if (!socket) return;
          socket.emit("join-channels", channelsJoined, user.id);
        }
        setJoinedRoom(true);
        fetchMessages(1);
      }
    } catch (err) {
      console.error("Failed to join channel:", err);
    }
  };

  //Fetch all chats
  const fetchMessages = async (currentPage = 1) => {
    try {
      const res = await api.get(
        ENDPOINTS.GET_MESSAGES(
          selectedChannel.id,
          user.id,
          currentPage,
          MESSAGES_PER_PAGE,
        ),
      );
      const fetched = res.data.messages || [];
      if (fetched.length < MESSAGES_PER_PAGE) setHasMoreMessages(false);
      if (currentPage === 1) {
        setMessages(fetched);

        //First Time scroll to latest chat
        setTimeout(() => {
          if (unreadRef.current) {
            unreadRef.current.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
          } else if (messageListRef.current != null) {
            messageListRef.current.scrollTo({
              top: messageListRef.current.scrollHeight,
              behavior: "auto",
            });
            setScrollToBottom(false);
          }
        }, 100); //to wait for DOM update
      } else {
        setMessages((prev) => [...fetched, ...prev]);
      }
    } catch (err) {
      console.error("Error fetching messages:", err);
    }
  };

  //Emoji Sender
  const handleEmojiClick = (emojiData) => {
    setNewMessage((prev) => prev + emojiData.emoji);
  };

  // //Send Message with protection against multiple sends
  // const handleSend = async () => {
  //   // Prevent sending if already sending
  //   if (isSending) return;

  //   // If there's an image preview, send with media instead
  //   if (imagePreview) {
  //     handleSendWithMedia();
  //     return;
  //   }

  //   // Check for empty message (only when no image)
  //   if (!newMessage.trim()) return;

  //   // Debounce protection - prevent rapid consecutive sends
  //   const now = Date.now();
  //   if (now - lastSendTime.current < SEND_DEBOUNCE_MS) {
  //     return;
  //   }
  //   lastSendTime.current = now;

  //   // Set sending state
  //   setIsSending(true);

  //   try {
  //     const payload = {
  //       userId: user.id,
  //       message: newMessage.trim(),
  //       fileType: "other",
  //       fileUrl: null,
  //       senderName: user.display_name || user.username,
  //     };

  //     const res = await api.post(
  //       ENDPOINTS.SEND_MESSAGE(selectedChannel.id),
  //       payload
  //     );

  //     if (res.data.success) {
  //       const messageToSend = newMessage.trim();
  //       setNewMessage("");
  //       setLinkPreview(null);

  //       socket.emit("send-message", {
  //         id: res.data.data.id,
  //         channelId: selectedChannel.id,
  //         userId: user.id,
  //         message: payload,
  //         channelDetails: channelDetails,
  //         sender_profile_picture: userMeta?.profile_picture_url || null,
  //       });

  //       setTimeout(() => {
  //         if (inputRef.current) {
  //           inputRef.current.focus();
  //         }
  //       }, 100);
  //     } else {
  //       toast.error("Failed to send message. Please try again.");
  //     }
  //   } catch (err) {
  //     console.error("Failed to send message:", err);
  //     toast.error("Failed to send message. Please try again.");
  //   } finally {
  //     setIsSending(false);
  //   }
  // };
  const handleSend = async () => {
    // Prevent sending if already sending
    if (isSending) return;

    // If there's an image preview, send with media instead
    if (imagePreview) {
      handleSendWithMedia();
      return;
    }

    // Check for empty message (only when no image)
    if (!newMessage.trim()) return;

    // Debounce protection - prevent rapid consecutive sends
    const now = Date.now();
    if (now - lastSendTime.current < SEND_DEBOUNCE_MS) {
      return;
    }
    lastSendTime.current = now;

    // Set sending state
    setIsSending(true);

    try {
      // Prepare reply message text
      let replyMessageText = null;
      if (replyingTo) {
        if (replyingTo.message && replyingTo.message.trim()) {
          replyMessageText = replyingTo.message;
        } else if (replyingTo.file_type) {
          replyMessageText = `[${replyingTo.file_type}]`;
        } else {
          replyMessageText = "[Message]";
        }
      }

      const payload = {
        userId: user.id,
        message: newMessage.trim(),
        fileType: "other",
        fileUrl: null,
        senderName: user.display_name || user.username,
        replyToMessageId: replyingTo?.id || null,
        replyToSenderId: replyingTo?.sender_id || null,
        replyToMessage: replyMessageText,
      };

      const res = await api.post(
        ENDPOINTS.SEND_MESSAGE(selectedChannel.id),
        payload,
      );

      if (res.data.success) {
        const messageToSend = newMessage.trim();
        setNewMessage("");
        setLinkPreview(null);
        setReplyingTo(null);

        socket.emit("send-message", {
          id: res.data.data.id,
          channelId: selectedChannel.id,
          userId: user.id,
          message: {
            userId: user.id,
            message: payload.message,
            fileType: payload.fileType,
            fileUrl: payload.fileUrl,
            senderName: payload.senderName,
            replyToMessageId: payload.replyToMessageId,
            replyToSenderId: payload.replyToSenderId,
            replyToMessage: payload.replyToMessage,
          },
          channelDetails: channelDetails,
          sender_profile_picture: userMeta?.profile_picture_url || null,
        });

        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.focus();
          }
        }, 100);
      } else {
        toast.error("Failed to send message. Please try again.");
      }
    } catch (err) {
      console.error("Failed to send message:", err);
      toast.error("Failed to send message. Please try again.");
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      if (e.shiftKey) {
        return;
      }
      e.preventDefault();
      if (canUserSendMessage() && !isSending) {
        if (imagePreview) {
          handleSendWithMedia();
        } else {
          handleSend();
        }
      }
    }
  };

  //Scroll top to fetch more data
  const handleScroll = () => {
    const container = messageListRef.current;
    if (!container || !hasMoreMessages) return;

    if (container.scrollTop === 0) {
      const prevHeight = container.scrollHeight;

      const nextPage = page + 1;
      setPage(nextPage);
      fetchMessages(nextPage).then(() => {
        requestAnimationFrame(() => {
          const newHeight = container.scrollHeight;
          container.scrollTop = newHeight - prevHeight;
        });
      });
    }
  };

  const ensureUserExists = async (userId) => {
    const exists = users.some((u) => u.id === userId);
    if (exists) return true;

    try {
      const { data } = await api.post(ENDPOINTS.GET_ALL_USERS_SIMPLE_BY_IDS, {
        user_ids: [userId],
      });
      if (data.success && Array.isArray(data.users)) {
        const newUsers = data.users.map((u) => ({
          id: u.id,
          username: u.username || u.name || "",
          display_name: u.display_name || "",
        }));
        let updatedUsersList = [...users, ...newUsers];
        setUsers(updatedUsersList);
        return true;
      }
    } catch (err) {
      console.error("Failed to fetch user:", err);
    }
    return false;
  };

  // //Media upload
  // const handleMediaUpload = async (e) => {
  //   const file = e.target.files[0];
  //   if (file) {
  //     setUploading(true); // show loading

  //     const formData = new FormData();
  //     formData.append("file", file);
  //     const res = await api.post(ENDPOINTS.UPLOAD_CHAT_MEDIA, formData);
  //     if (res.data.success) {
  //       const messagePayload = {
  //         userId: user.id,
  //         message: newMessage.trim(),
  //         fileType: res.data.data.media_type || "other",
  //         fileUrl: res.data.data.file_url,
  //         senderName: user.display_name || user.username,
  //       };
  //       const resMessage = await api.post(
  //         ENDPOINTS.SEND_MESSAGE(selectedChannel.id),
  //         messagePayload
  //       );
  //       if (resMessage.data.success) {
  //         setNewMessage("");
  //         socket.emit("send-message", {
  //           id: resMessage.data.data.id,
  //           channelId: selectedChannel.id,
  //           userId: user.id,
  //           message: messagePayload,
  //           channelDetails: channelDetails,
  //           sender_profile_picture: userMeta?.profile_picture_url || null,
  //         });

  //         // Focus the input field after successful media send
  //         setTimeout(() => {
  //           if (inputRef.current) {
  //             inputRef.current.focus();
  //           }
  //         }, 100);
  //       }
  //     }

  //     setUploading(false); // hide loading
  //   }
  // };

  //Media upload - just show preview, don't upload yet
  const handleMediaUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);

      // Create preview URL for images
      if (file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setImagePreview(reader.result);
        };
        reader.readAsDataURL(file);
      } else {
        // For non-image files, show file info
        setImagePreview("file");
      }

      // Reset file input so same file can be selected again
      e.target.value = "";
    }
  };

  // Cancel image preview
  const handleCancelPreview = () => {
    setImagePreview(null);
    setSelectedFile(null);
    // Don't clear newMessage - user might have typed text already
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Send message with media
  // const handleSendWithMedia = async () => {
  //   if (!selectedFile || isSending) return;

  //   setIsSending(true);
  //   setUploading(true);

  //   try {
  //     const formData = new FormData();
  //     formData.append("file", selectedFile);

  //     const res = await api.post(ENDPOINTS.UPLOAD_CHAT_MEDIA, formData);

  //     if (res.data.success) {
  //       const messagePayload = {
  //         userId: user.id,
  //         message: newMessage.trim(), // Use newMessage instead of previewCaption
  //         fileType: res.data.data.media_type || "other",
  //         fileUrl: res.data.data.file_url,
  //         senderName: user.display_name || user.username,
  //       };

  //       const resMessage = await api.post(
  //         ENDPOINTS.SEND_MESSAGE(selectedChannel.id),
  //         messagePayload
  //       );

  //       if (resMessage.data.success) {
  //         setNewMessage("");
  //         handleCancelPreview();

  //         socket.emit("send-message", {
  //           id: resMessage.data.data.id,
  //           channelId: selectedChannel.id,
  //           userId: user.id,
  //           message: messagePayload,
  //           channelDetails: channelDetails,
  //           sender_profile_picture: userMeta?.profile_picture_url || null,
  //         });

  //         setTimeout(() => {
  //           if (inputRef.current) {
  //             inputRef.current.focus();
  //           }
  //         }, 100);
  //       }
  //     }
  //   } catch (error) {
  //     console.error("Failed to upload media:", error);
  //     toast.error("Failed to upload media. Please try again.");
  //   } finally {
  //     setUploading(false);
  //     setIsSending(false);
  //   }
  // };

  const handleSendWithMedia = async () => {
    if (!selectedFile || isSending) return;

    setIsSending(true);
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append("file", selectedFile);

      const res = await api.post(ENDPOINTS.UPLOAD_CHAT_MEDIA, formData);

      if (res.data.success) {
        // Prepare reply message text
        let replyMessageText = null;
        if (replyingTo) {
          if (replyingTo.message && replyingTo.message.trim()) {
            replyMessageText = replyingTo.message;
          } else if (replyingTo.file_type) {
            replyMessageText = `[${replyingTo.file_type}]`;
          } else {
            replyMessageText = "[Message]";
          }
        }

        const messagePayload = {
          userId: user.id,
          message: newMessage.trim(),
          fileType: res.data.data.media_type || "other",
          fileUrl: res.data.data.file_url,
          senderName: user.display_name || user.username,
          replyToMessageId: replyingTo?.id || null,
          replyToSenderId: replyingTo?.sender_id || null,
          replyToMessage: replyMessageText,
        };

        const resMessage = await api.post(
          ENDPOINTS.SEND_MESSAGE(selectedChannel.id),
          messagePayload,
        );

        if (resMessage.data.success) {
          setNewMessage("");
          handleCancelPreview();
          setReplyingTo(null);

          socket.emit("send-message", {
            id: resMessage.data.data.id,
            channelId: selectedChannel.id,
            userId: user.id,
            message: {
              userId: messagePayload.userId,
              message: messagePayload.message,
              fileType: messagePayload.fileType,
              fileUrl: messagePayload.fileUrl,
              senderName: messagePayload.senderName,
              replyToMessageId: messagePayload.replyToMessageId,
              replyToSenderId: messagePayload.replyToSenderId,
              replyToMessage: messagePayload.replyToMessage,
            },
            channelDetails: channelDetails,
            sender_profile_picture: userMeta?.profile_picture_url || null,
          });

          setTimeout(() => {
            if (inputRef.current) {
              inputRef.current.focus();
            }
          }, 100);
        }
      }
    } catch (error) {
      console.error("Failed to upload media:", error);
      toast.error("Failed to upload media. Please try again.");
    } finally {
      setUploading(false);
      setIsSending(false);
    }
  };
  // Toggle Chat Permission
  const toggleChatPermission = async () => {
    try {
      const { data } = await api.put(
        ENDPOINTS.TOGGLE_CHAT_PERMISSION(selectedChannel.id),
      );
      if (data.success) {
        toast.success(data.message || "permission updated successfully");
        socket.emit("chat-permission-toggle", {
          channelId: selectedChannel.id,
          allow_messages: selectedChannel.allow_messages ? false : true,
        });
      } else {
        toast.error(data.message || "Something went wrong");
      }
    } catch (err) {
      toast.error("Error updating chat permission");
    }
  };

  // Toggle Chat Notification Permission
  const toggleChatNotificationPermission = async () => {
    try {
      const { data } = await api.post(ENDPOINTS.TOGGLE_CHAT_NOTIFICATION, {
        channelId: selectedChannel.id,
        userId: user.id,
      });
      if (data.success) {
        toast.success(data.message || "notification updated successfully");
        socket.emit("chat-notification-toggle", {
          userId: user.id,
          channelId: selectedChannel.id,
          enable_notification: data.enable_notification,
        });
      } else {
        toast.error(data.message || "Something went wrong");
      }
    } catch (err) {
      toast.error("Error updating chat notification");
    }
  };

  const fetchSearchResults = async (keyword) => {
    try {
      const res = await api.get(
        ENDPOINTS.SEARCH_MESSAGES(selectedChannel.id, keyword),
      );
      setSearchResults(res.data.messages || []);
    } catch (err) {
      console.error("Search failed:", err);
    }
  };

  const jumpToMessage = async (messageId) => {
    try {
      const res = await api.get(
        ENDPOINTS.GET_MESSAGES_BY_CONTEXT(
          selectedChannel.id,
          user.id,
          messageId,
          10,
          10,
        ),
      );
      const msgs = res.data.messages || [];
      setContextMessages(msgs);
      setSearchMode(true);
      setContextTarget(messageId);
      setHighlightedMessageId(messageId); // Set the message to highlight

      setTimeout(() => {
        setSearch("");
        const el = document.getElementById(`msg-${messageId}`);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });

          // Remove highlight after 3 seconds
          setTimeout(() => {
            setHighlightedMessageId(null);
          }, 3000);
        }
      }, 100);
    } catch (err) {
      console.error("Jump failed:", err);
    }
  };

  const loadMoreContext = async (direction) => {
    if (!contextTarget) return;

    const anchorId =
      direction === "up"
        ? contextMessages[0]?.id
        : contextMessages[contextMessages.length - 1]?.id;

    try {
      const res = await api.get(
        ENDPOINTS.GET_MESSAGES_BY_CONTEXT(
          selectedChannel.id,
          user.id,
          anchorId,
          direction === "up" ? 10 : 0,
          direction === "down" ? 10 : 0,
        ),
      );

      const newMsgs = res.data.messages || [];
      if (newMsgs.length === 0) return;

      setContextMessages((prev) => {
        // merge old + new
        const merged =
          direction === "up" ? [...newMsgs, ...prev] : [...prev, ...newMsgs];

        // remove duplicates by id
        const unique = Array.from(
          new Map(merged.map((m) => [m.id, m])).values(),
        );

        // keep chronological order (if needed)
        return unique.sort((a, b) => a.id - b.id);
      });
    } catch (err) {
      console.error("Load more context failed:", err);
    }
  };

  //Scroll top to fetch more search data
  const handleSearchScroll = () => {
    const container = messageListRef.current;
    if (!container) return;

    const top = container.scrollTop;
    const bottom = container.scrollHeight - top - container.clientHeight;

    if (top === 0) {
      const prevHeight = container.scrollHeight;

      loadMoreContext("up").then(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const newHeight = container.scrollHeight;
            container.scrollTop = newHeight - prevHeight;
          });
        });
      });
    } else if (bottom <= 10) {
      loadMoreContext("down");
    }
  };

  //Scroll to latest chat
  useEffect(() => {
    if (!messageListRef.current) return;
    if (!scrollToBottom) return;

    const timeout = setTimeout(() => {
      messageListRef.current.scrollTo({
        top: messageListRef.current.scrollHeight,
        behavior: "smooth",
      });
      setScrollToBottom(false);
    }, 50); //to wait for DOM update

    return () => clearTimeout(timeout);
  }, [scrollToBottom]);

  //Check if you have access to the channel
  useEffect(() => {
    if (selectedChannel?.id) {
      const checkAccess = async () => {
        setLoading(true);
        try {
          const { id, is_paid } = selectedChannel;
          const role = user?.roles?.[0];

          // ALWAYS fetch channel data on every channel selection
          const { data } = await api.get(ENDPOINTS.GET_CHANNEL_BY_ID(id));
          const channelData = data.channel || {};
          // console.log("Channel data:", channelData);
          setChannelDetails(channelData);

          let accessible = false;
          let isAssignedTrader = false;

          // Paid channel → check memberships
          if (is_paid === 1) {
            const membershipsAllowed = channelData.membership_ids || [];

            // ✅ IAP users get full access (paid from mobile app)
            if (userMeta?.has_active_iap === true) {
              accessible = true;
            } else {
              // Existing WordPress membership check
              let userMembershipIds = [];
              if (Array.isArray(userMeta?.membership_ids)) {
                userMembershipIds = userMeta.membership_ids.map(Number);
              } else if (typeof userMeta?.membership_ids === "string") {
                try {
                  userMembershipIds = JSON.parse(userMeta.membership_ids).map(
                    Number,
                  );
                } catch {
                  userMembershipIds = [];
                }
              }
              accessible = membershipsAllowed.some((id) =>
                userMembershipIds.includes(id),
              );
            }

            const tradersAllowed = channelData.trader_ids || [];
            isAssignedTrader = tradersAllowed.includes(user.id);
          } else {
            accessible = true;
          }

          // Trader role check
          if (role === "trader") {
            const tradersAllowed = channelData.trader_ids || [];
            isAssignedTrader = tradersAllowed.includes(user.id);

            if (!isAssignedTrader && selectedChannel.community) {
              try {
                const communityChannels = channels.filter(
                  (channel) =>
                    channel.community === selectedChannel.community &&
                    channel.id !== selectedChannel.id,
                );

                if (communityChannels.length > 0) {
                  // Single API call for all channels
                  const channelIds = communityChannels.map((c) => c.id);
                  const { data } = await api.post(
                    ENDPOINTS.GET_CHANNELS_BY_IDS,
                    { channelIds },
                  );

                  const isAssignedToAnyCommunityChannel = data.channels.some(
                    (channel) => {
                      const traderIds = channel.trader_ids || [];
                      return traderIds.includes(user.id);
                    },
                  );

                  if (isAssignedToAnyCommunityChannel) {
                    accessible = true;
                  }
                }
              } catch (err) {
                console.error("Error checking community channels:", err);
              }
            }
          }
          // Admin or assigned trader always accessible
          if (
            role === "administrator" ||
            (role === "trader" && isAssignedTrader)
          ) {
            accessible = true;
          }

          setIsAccessible(accessible);
          setAmIAssignedToThisChannel(isAssignedTrader);
        } catch (err) {
          console.error("Error fetching channel details:", err);
          setIsAccessible(false);
        } finally {
          setLoading(false);
        }
      };

      checkAccess();
      checkJoined();
      setPage(1);
      setHasMoreMessages(true);
      setMessages([]);
    }
  }, [
    selectedChannel?.id,
    userMeta?.membership_ids,
    selectedChannel.is_paid,
    user?.id,
  ]);

  //Automatically fetch messages after joining a channel
  useEffect(() => {
    if (joinedRoom) {
      fetchMessages(1);
    }
  }, [joinedRoom, selectedChannel.id]);

  // Get Live Messages
  // useEffect(() => {
  //   if (!newMessageRecieved) return;

  //   const handleIncomingMessage = async () => {
  //     if (selectedChannel.id === newMessageRecieved.channelId) {
  //       await ensureUserExists(newMessageRecieved.message.userId);

  //       const formattedMessage = {
  //         id: newMessageRecieved.id,
  //         message: newMessageRecieved.message.message,
  //         file_type: newMessageRecieved.message.fileType,
  //         file_url: newMessageRecieved.message.fileUrl,
  //         created_at: newMessageRecieved.createdAt,
  //         sender_id: newMessageRecieved.message.userId,
  //         is_deleted: 0,
  //         reactions: [],
  //         sender_profile_picture:
  //           newMessageRecieved.sender_profile_picture || null,
  //       };

  //       setMessages((prev) => [...prev, formattedMessage]);
  //       setScrollToBottom(true);
  //       setNewMessageRecieved(null);
  //       setRetainedUnreadCount(null);

  //       // Mark as read
  //       if (selectedChannel?.id) {
  //         await api.post(ENDPOINTS.MARK_CHANNEL_AS_READ, {
  //           userId: user.id,
  //           channelId: selectedChannel.id,
  //         });

  //         setUnreadChatCounts((prev) => ({
  //           ...prev,
  //           [selectedChannel.id]: 0,
  //         }));
  //       }
  //     }
  //   };

  //   handleIncomingMessage();
  // }, [newMessageRecieved, selectedChannel.id]);

  // Get Live Messages
  useEffect(() => {
    if (!newMessageRecieved) return;

    const handleIncomingMessage = async () => {
      if (selectedChannel.id === newMessageRecieved.channelId) {
        await ensureUserExists(newMessageRecieved.message.userId);

        const formattedMessage = {
          id: newMessageRecieved.id,
          message: newMessageRecieved.message.message,
          file_type: newMessageRecieved.message.fileType,
          file_url: newMessageRecieved.message.fileUrl,
          created_at: newMessageRecieved.createdAt,
          sender_id: newMessageRecieved.message.userId,
          is_deleted: 0,
          reactions: [],
          sender_profile_picture:
            newMessageRecieved.sender_profile_picture || null,
          // Add reply data
          reply_to_message_id:
            newMessageRecieved.message.replyToMessageId || null,
          reply_to_sender_id:
            newMessageRecieved.message.replyToSenderId || null,
          reply_to_message: newMessageRecieved.message.replyToMessage || null,
          reply_to_sender_profile_picture: null, // Will be fetched if needed
        };

        setMessages((prev) => [...prev, formattedMessage]);
        setScrollToBottom(true);
        setNewMessageRecieved(null);
        setRetainedUnreadCount(null);

        // Mark as read
        if (selectedChannel?.id) {
          await api.post(ENDPOINTS.MARK_CHANNEL_AS_READ, {
            userId: user.id,
            channelId: selectedChannel.id,
          });

          setUnreadChatCounts((prev) => ({
            ...prev,
            [selectedChannel.id]: 0,
          }));
        }
      }
    };

    handleIncomingMessage();
  }, [newMessageRecieved, selectedChannel.id]);
  useEffect(() => {
    const markAsRead = async () => {
      if (selectedChannel?.id) {
        await api.post(ENDPOINTS.MARK_CHANNEL_AS_READ, {
          userId: user.id,
          channelId: selectedChannel.id,
        });

        setUnreadChatCounts((prev) => ({
          ...prev,
          [selectedChannel.id]: 0,
        }));
      }
    };

    markAsRead();
  }, [selectedChannel.id, user.id, setUnreadChatCounts]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        emojiPickerRef.current &&
        !emojiPickerRef.current.contains(event.target)
      ) {
        setShowEmojiPicker(false);
      }
    };

    if (showEmojiPicker) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showEmojiPicker]);

  //Get Live Channel Permission
  useEffect(() => {
    if (!newChatPermissionRecieved) return;
    if (selectedChannel.id === newChatPermissionRecieved.channelId) {
      setSelectedChannel({
        ...selectedChannel,
        allow_messages: newChatPermissionRecieved.allow_messages,
      });
      setNewChatPermissionRecieved(null);

      //Update the main channels list
      let newChannelsList = [...channels];
      const index = newChannelsList.findIndex(
        (c) => c.id === selectedChannel.id,
      );
      if (index !== -1) {
        newChannelsList[index].allow_messages =
          newChatPermissionRecieved.allow_messages;
      }
      setChannels(newChannelsList);
    }
  }, [
    newChatPermissionRecieved,
    setNewChatPermissionRecieved,
    selectedChannel.id,
    selectedChannel,
    setSelectedChannel,
    channels,
    setChannels,
  ]);

  //Get Live Notification Permission
  useEffect(() => {
    if (!newChatNotificationRecieved) return;
    if (
      selectedChannel.id === newChatNotificationRecieved.channelId &&
      newChatNotificationRecieved.userId === user.id
    ) {
      let newNotificationsList = [...notificationPermissionList];
      const index = notificationPermissionList.findIndex(
        (c) => c.channel_id === selectedChannel.id,
      );
      if (index !== -1) {
        newNotificationsList[index].enable_notification =
          newChatNotificationRecieved.enable_notification ? 1 : 0;
      } else {
        newNotificationsList = [
          ...notificationPermissionList,
          {
            channel_id: selectedChannel.id,
            enable_notification: newChatNotificationRecieved.enable_notification
              ? 1
              : 0,
          },
        ];
      }
      setNewChatNotificationRecieved(null);
      setNotificationPermissionList(newNotificationsList);
    }
  }, [
    selectedChannel.id,
    user.id,
    newChatNotificationRecieved,
    setNewChatNotificationRecieved,
    notificationPermissionList,
    setNotificationPermissionList,
  ]);

  useEffect(() => {
    if (selectedChannel?.id) {
      setRetainedUnreadCount(unreadChatCounts[selectedChannel.id] ?? 0);
    }
  }, [selectedChannel?.id]);
  // --- detect links while typing ---
  useEffect(() => {
    const url = extractFirstUrl(newMessage);
    if (!url) {
      setLinkPreview(null);
      return;
    }

    const fetchPreview = async () => {
      try {
        setIsPreviewLoading(true);
        const { data } = await api.get(ENDPOINTS.GET_LINK_PREVIEW(url));

        if (data.success) {
          setLinkPreview(data.preview);
        } else {
          setLinkPreview(null);
        }
      } catch (err) {
        console.error("Preview fetch error", err);
        setLinkPreview(null);
      } finally {
        setIsPreviewLoading(false);
      }
    };

    // Debounce the preview fetch
    const timeoutId = setTimeout(() => {
      fetchPreview();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [newMessage]);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (search.trim() !== "") {
        fetchSearchResults(search);
      } else {
        setSearchResults([]); // clear if search is empty
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(delayDebounce);
  }, [search]);

  if (loading || !userMeta) {
    return (
      <div className={styles.chatContainer}>
        <div className={styles.restrictedContainer}>
          {loading ? "Checking access..." : "Loading user info..."}
        </div>
      </div>
    );
  }

  let isMuted =
    notificationPermissionList.find((e) => e.channel_id === selectedChannel.id)
      ?.enable_notification === 0;

  return (
    <div className={styles.chatContainer}>
      {!isAccessible ? (
        <div className={styles.restrictedContainer}>
          <h3>Channel Restricted</h3>
          <p>
            This channel requires a membership to access. Please purchase one
            below.
          </p>

          <button
            onClick={handleMembershipClick}
            className={styles.membershipLink}
            disabled={isPurchasing || isLoadingPlans}
          >
            {isLoadingPlans ? (
              <>
                <FaSpinner className={styles.spinner} />
                Loading Plans...
              </>
            ) : isPurchasing ? (
              <>
                <FaSpinner className={styles.spinner} />
                Processing...
              </>
            ) : (
              "Buy Membership"
            )}
          </button>
          {/* Plan Selection Modal */}
          {showPlanSelection && (
            <div className={styles.modalOverlay}>
              <div className={styles.modalContent}>
                <h3>Choose a Membership Plan</h3>
                <p>This channel offers multiple membership options:</p>

                <div className={styles.plansList}>
                  {availablePlans.map((plan) => (
                    <div key={plan.id} className={styles.planCard}>
                      <div className={styles.planInfo}>
                        <h4>{plan.name || plan.label || `Plan ${plan.id}`}</h4>
                        <div className={styles.planPrice}>
                          {plan.payment_type === "free"
                            ? "Free"
                            : `$${plan.price}`}
                        </div>
                        {plan.short_description && (
                          <p className={styles.planDescription}>
                            {plan.short_description}
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() => {
                          handlePurchaseMembership(plan.id);
                          setShowPlanSelection(false);
                        }}
                        className={styles.planSelectButton}
                        disabled={isPurchasing}
                      >
                        {isPurchasing ? (
                          <>
                            <FaSpinner className={styles.spinner} />
                            Processing...
                          </>
                        ) : (
                          "Select This Plan"
                        )}
                      </button>
                    </div>
                  ))}
                </div>

                <button
                  onClick={() => setShowPlanSelection(false)}
                  className={styles.cancelButton}
                  disabled={isPurchasing}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      ) : !joinedRoom ? (
        <div className={styles.restrictedContainer}>
          <h3>You're not in this channel</h3>
          <p>To join and start chatting, click below:</p>
          <button className={styles.sendButton} onClick={joinChannel}>
            Join Channel
          </button>
        </div>
      ) : (
        <>
          {/* App Bar */}
          <div className={styles.appBar}>
            <h2 className={styles.title}>{selectedChannel.name}</h2>

            {/* Group both toggle buttons together */}
            <div className={styles.buttonGroup}>
              {/* Chat Permission Button */}
              {user?.roles[0] === "administrator" && (
                <div className={styles.toggleWrapper}>
                  <button
                    className={`${styles.toggleOption} ${
                      selectedChannel.allow_messages ? styles.activeToggle : ""
                    }`}
                    onClick={toggleChatPermission}
                  >
                    {selectedChannel.allow_messages ? <FaUnlock /> : <FaLock />}
                  </button>
                </div>
              )}

              {/* Notification Button */}
              <div className={styles.toggleWrapper}>
                <button
                  className={`${styles.toggleOption} ${
                    !isMuted ? styles.activeToggle : ""
                  }`}
                  onClick={toggleChatNotificationPermission}
                >
                  {!isMuted ? <FaVolumeUp /> : <FaVolumeMute />}
                </button>
              </div>
            </div>

            {/* Search Bar */}
            <div className={styles.searchContainer} ref={searchRef}>
              <input
                type="text"
                placeholder="Type in to search .."
                className={styles.searchInput}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <FiSearch className={styles.searchIcon} />

              {/* Search Popup */}
              {search && searchResults.length > 0 && (
                <div className={styles.searchPopup}>
                  {searchResults.map((msg) => (
                    <div
                      key={msg.id}
                      className={styles.searchResultItem}
                      onClick={() => jumpToMessage(msg.id)}
                    >
                      <div className={styles.resultHeader}>
                        <span className={styles.resultUser}>
                          {users.find((e) => e.id === msg.sender_id)
                            ?.username || "..."}
                        </span>
                        <span className={styles.resultTime}>
                          {formatDateTimeParts(msg.created_at).date}{" "}
                          {formatDateTimeParts(msg.created_at).time}
                        </span>
                      </div>
                      <div className={styles.resultSnippet}>{msg.message}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Channel Info Section */}
          {showChannelInfo && channelDetails && (
            <div className={styles.channelInfoContainer}>
              <div className={styles.channelInfoContent}>
                {/* Banner with Icon */}
                <div className={styles.channelBanner}>
                  {channelDetails.banner && (
                    <img
                      src={`${ENDPOINTS.IMAGE_BASE_URL}${channelDetails.banner}`}
                      alt={channelDetails.name}
                      className={styles.bannerImage}
                    />
                  )}
                  {channelDetails.icon && (
                    <div className={styles.channelIconWrapper}>
                      <img
                        src={`${ENDPOINTS.IMAGE_BASE_URL}${channelDetails.icon}`}
                        alt={`${channelDetails.name} icon`}
                        className={styles.channelIcon}
                      />
                    </div>
                  )}
                </div>

                {/* Description */}
                {channelDetails.description && (
                  <div
                    className={styles.channelDescription}
                    onClick={() => setShowChannelInfoModal(true)}
                  >
                    <p>{channelDetails.description}</p>
                    <button className={styles.readMoreBtn}>
                      <FaInfoCircle /> Read More
                    </button>
                  </div>
                )}
              </div>

              {/* Toggle Button */}
              <button
                className={styles.channelInfoToggle}
                onClick={toggleChannelInfo}
                title="Hide channel info"
              >
                <FaChevronUp />
              </button>
            </div>
          )}

          {/* Show Channel Info Button (when hidden) */}
          {!showChannelInfo && (
            <button
              className={styles.showChannelInfoBtn}
              onClick={toggleChannelInfo}
              title="Show channel info"
            >
              <FaChevronDown /> Show Channel Info
            </button>
          )}

          {/* Channel Info Modal */}
          {showChannelInfoModal && (
            <div
              className={styles.modalOverlay}
              onClick={() => setShowChannelInfoModal(false)}
            >
              <div
                className={styles.modalContent}
                onClick={(e) => e.stopPropagation()}
              >
                <div className={styles.modalHeader}>
                  <h3>{channelDetails.name}</h3>
                  <button
                    className={styles.modalCloseBtn}
                    onClick={() => setShowChannelInfoModal(false)}
                  >
                    ✕
                  </button>
                </div>

                {channelDetails.banner && (
                  <div className={styles.modalBanner}>
                    <img
                      src={`${ENDPOINTS.IMAGE_BASE_URL}${channelDetails.banner}`}
                      alt={channelDetails.name}
                      className={styles.modalBannerImage}
                    />
                    {channelDetails.icon && (
                      <div className={styles.modalIconWrapper}>
                        <img
                          src={`${ENDPOINTS.IMAGE_BASE_URL}${channelDetails.icon}`}
                          alt={`${channelDetails.name} icon`}
                          className={styles.modalIcon}
                        />
                      </div>
                    )}
                  </div>
                )}

                <div className={styles.modalBody}>
                  <h4>Description</h4>
                  <p>{channelDetails.description}</p>

                  {channelDetails.community && (
                    <div className={styles.modalInfo}>
                      <strong>Community:</strong>{" "}
                      {channelDetails.community.name}
                    </div>
                  )}

                  {channelDetails.section && (
                    <div className={styles.modalInfo}>
                      <strong>Section:</strong> {channelDetails.section.title}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* SEARCH MESSAGE LIST */}
          {searchMode && (
            <div
              className={styles.messageList}
              onScroll={handleSearchScroll}
              ref={messageListRef}
            >
              {contextMessages.map((msg, i) => (
                <div
                  key={i}
                  id={`msg-${msg.id}`}
                  className={`${styles.messageItem} ${
                    highlightedMessageId === msg.id
                      ? styles.highlightedMessage
                      : ""
                  }`}
                >
                  <MessageCard
                    sender={(() => {
                      const sender = users.find((e) => e.id === msg.sender_id);
                      return sender?.display_name || sender?.username || "...";
                    })()}
                    messageData={msg}
                    channel={{
                      ...selectedChannel,
                      onJumpToMessage: jumpToMessageFromReply,
                    }}
                    currentUserId={user.id}
                    currentUserRole={user?.roles[0]}
                    onEdit={handleEditMessage}
                    onDelete={handleDeleteMessage}
                    onReply={setReplyingTo}
                    users={users}
                  />
                </div>
              ))}
            </div>
          )}
          {/* NORMAL MESSAGE LIST */}
          {!searchMode && (
            <div
              className={styles.messageList}
              onScroll={handleScroll}
              ref={messageListRef}
            >
              {messages.map((msg, i) => (
                <div
                  key={i}
                  id={`msg-${msg.id}`}
                  className={styles.messageItem}
                >
                  {search === "" &&
                    retainedUnreadCount > 0 &&
                    messages.length - retainedUnreadCount === i && (
                      <div ref={unreadRef} className={styles.unreadCount}>
                        {retainedUnreadCount} unread message
                      </div>
                    )}
                  <MessageCard
                    sender={(() => {
                      const sender = users.find((e) => e.id === msg.sender_id);
                      return sender?.display_name || sender?.username || "...";
                    })()}
                    messageData={msg}
                    channel={{
                      ...selectedChannel,
                      onJumpToMessage: jumpToMessageFromReply,
                    }}
                    currentUserId={user.id}
                    currentUserRole={user?.roles[0]}
                    onEdit={handleEditMessage}
                    onDelete={handleDeleteMessage}
                    onReply={setReplyingTo}
                    users={users}
                  />
                </div>
              ))}
            </div>
          )}

          {/* Footer Buttons */}
          <div className={styles.footerContainer}>
            {/* Reset Search Mode */}
            {searchMode && (
              <button
                className={styles.scrollToBottomBtn}
                onClick={() => {
                  setSearch("");
                  setSearchMode(false);
                  setContextMessages([]);
                  setContextTarget(null);
                  setHighlightedMessageId(null); // Clear highlight when exiting search mode
                  setTimeout(() => {
                    setScrollToBottom(true);
                  }, 100);
                }}
              >
                <FaArrowDown />
              </button>
            )}

            {/* Message Preview before send */}
            {isPreviewLoading && (
              <div className={styles.previewBox}>
                <p>Loading preview...</p>
              </div>
            )}
            {linkPreview && !isPreviewLoading && (
              <div className={styles.previewBox}>
                {linkPreview.image && (
                  <img
                    src={linkPreview.image}
                    alt="preview"
                    className={styles.previewImage}
                  />
                )}
                <div className={styles.previewContent}>
                  <div className={styles.previewTitle}>{linkPreview.title}</div>
                  <div className={styles.previewDesc}>
                    {linkPreview.description}
                  </div>
                  <a
                    href={linkPreview.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.previewUrl}
                  >
                    {linkPreview.url}
                  </a>
                </div>
              </div>
            )}
            {/* Reply Preview */}
            {replyingTo && (
              <div className={styles.replyPreview}>
                <div className={styles.replyContent}>
                  <div className={styles.replyLabel}>
                    Replying to{" "}
                    {users.find((u) => u.id === replyingTo.sender_id)
                      ?.display_name ||
                      users.find((u) => u.id === replyingTo.sender_id)
                        ?.username ||
                      "User"}
                  </div>
                  <div className={styles.replyMessage}>
                    {replyingTo.message && replyingTo.message.trim()
                      ? replyingTo.message
                      : replyingTo.file_type
                        ? `[${replyingTo.file_type}]`
                        : "[Message]"}
                  </div>
                </div>
                <button
                  className={styles.cancelReplyBtn}
                  onClick={() => setReplyingTo(null)}
                  title="Cancel reply"
                >
                  <FaTimes />
                </button>
              </div>
            )}
            {/* Image Preview - Inline above input */}
            {imagePreview && (
              <div className={styles.inlinePreviewContainer}>
                <div className={styles.inlinePreviewContent}>
                  {imagePreview !== "file" ? (
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className={styles.inlinePreviewImage}
                    />
                  ) : (
                    <div className={styles.inlineFilePreview}>
                      <span>📄 {selectedFile?.name}</span>
                      <span className={styles.inlineFileSize}>
                        {(selectedFile?.size / 1024).toFixed(2)} KB
                      </span>
                    </div>
                  )}
                  <button
                    onClick={handleCancelPreview}
                    className={styles.inlinePreviewClose}
                    disabled={uploading}
                    title="Remove"
                  >
                    <FaTimes />
                  </button>
                </div>
              </div>
            )}

            {/* Send Message Box */}

            {/* Send Message Box */}
            <div className={styles.inputContainer}>
              {/* Message Box */}
              <textarea
                ref={inputRef}
                placeholder="Type your message..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                className={styles.inputBox}
                onKeyDown={handleKeyDown}
                disabled={!canUserSendMessage() || isSending}
                rows={1}
              />
              {/* Media Upload */}
              <input
                ref={fileInputRef}
                type="file"
                id="mediaUpload"
                accept="
                  image/*,
                  video/*,
                  application/pdf,
                  application/msword,
                  application/vnd.openxmlformats-officedocument.wordprocessingml.document,
                  application/vnd.ms-excel,
                  application/vnd.openxmlformats-officedocument.spreadsheetml.sheet
                "
                style={{ display: "none" }}
                onChange={handleMediaUpload}
                disabled={!canUserSendMessage() || uploading || isSending}
              />
              <label
                htmlFor="mediaUpload"
                className={`${styles.sendButton} ${
                  uploading || isSending ? styles.disabled : ""
                }`}
              >
                {uploading ? <FaSpinner className={styles.spinner} /> : "+"}
              </label>

              {/* Emoji Picker */}
              <div style={{ position: "relative" }}>
                <button
                  className={`${styles.sendButton} ${
                    isSending ? styles.disabled : ""
                  }`}
                  onClick={() => setShowEmojiPicker((prev) => !prev)}
                  disabled={!canUserSendMessage() || isSending}
                >
                  😀
                </button>
                {showEmojiPicker && (
                  <div
                    ref={emojiPickerRef}
                    className={styles.emojiPickerWrapper}
                  >
                    <EmojiPicker onEmojiClick={handleEmojiClick} />
                  </div>
                )}
              </div>

              {/* Send Button */}
              <button
                className={`${styles.sendButton} ${
                  isSending ? styles.sending : ""
                }`}
                onClick={handleSend}
                disabled={!canUserSendMessage() || isSending}
              >
                {isSending ? <FaSpinner className={styles.spinner} /> : "Send"}
              </button>
            </div>

            {/* Overlay To Disable Chat*/}
            {!canUserSendMessage() && <div className={styles.overlay}></div>}
          </div>
        </>
      )}
    </div>
  );
};

export default ChatSection;
